import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import i18n from "../i18n";
import { api, type Step } from "../api";

type LiveRun = {
  run_id: number | null;
  name: string;
  bench_name: string;
  bench_id: number;
  started_at: number;
  step_count: number;
  step: Step | null;
  starting?: boolean;
};

/** How long this one has been going. Not `duration()`: that one rounds to
 *  whole minutes, and the first ninety seconds of a run are exactly when
 *  somebody is watching this bar. */
function elapsed(from: number): string {
  const s = Math.max(0, Math.floor(Date.now() / 1000 - from));
  if (s < 90) return i18n.t("live.elapsedSeconds", { s });
  if (s < 5400) return i18n.t("live.elapsedMinutes", { m: Math.floor(s / 60) });
  return i18n.t("live.elapsedHours", { h: (s / 3600).toFixed(1) });
}

/**
 * What the rigs are doing, on every page.
 *
 * "Is anything running" is the question people open this service to answer, and
 * before this they had to guess which bench, open it, and read a table. It sits
 * in the shell rather than on one page for the same reason: the answer matters
 * most when you are somewhere else, about to take a bench.
 *
 * Silent when nothing is running - a banner that is always there stops being
 * read, and an idle fleet is the normal case.
 */
export default function LiveBar() {
  const { t } = useTranslation();
  const [runs, setRuns] = useState<LiveRun[]>([]);

  useEffect(() => {
    let alive = true;
    const pull = () =>
      api
        .live()
        .then((d) => alive && setRuns(d.live))
        // Deliberately silent: this is ambient, and an error banner for a
        // background poll would cover the page somebody is actually using.
        .catch(() => {});
    void pull();
    const t = setInterval(pull, 10000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (runs.length === 0) return null;

  return (
    <div className="border-b border-sky-200 bg-sky-50 dark:border-sky-900 dark:bg-sky-950/50">
      <div className="mx-auto max-w-5xl px-4 py-2">
        {runs.map((r) => {
          let action = "";
          let stepNo: number | null = null;
          let total: number | null = null;
          if (r.step) {
            action = r.step.action || "";
            stepNo = r.step.step_no;
            try {
              total = (JSON.parse(r.step.raw_json) as { total?: number }).total ?? null;
            } catch {
              total = null;
            }
          }
          return (
            <div
              key={`${r.run_id ?? "job"}-${r.name}-${r.bench_id}`}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm"
            >
              <span className="relative flex h-2 w-2 shrink-0 translate-y-[-1px]">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-500" />
              </span>
              {r.run_id ? (
                <Link
                  to={`/runs/${r.run_id}`}
                  className="font-medium text-sky-900 underline dark:text-sky-200"
                >
                  {r.name}
                </Link>
              ) : (
                // Nothing to link to yet: the run has not opened on the service
                // because the harness is still running its own gate.
                <span className="font-medium text-sky-900 dark:text-sky-200">
                  {r.name}
                </span>
              )}
              <span className="text-sky-900/70 dark:text-sky-200/70">
                <Trans
                  i18nKey="live.onBench"
                  values={{ bench: r.bench_name }}
                  components={{
                    benchLink: (
                      <Link to={`/benches/${r.bench_id}`} className="underline" />
                    ),
                  }}
                />
              </span>
              {r.step && (
                <span className="min-w-0 truncate text-sky-900/70 dark:text-sky-200/70">
                  · {r.step.scenario}{" "}
                  {total
                    ? t("live.stepOf", { no: stepNo ?? t("live.unknownStep"), total })
                    : t("live.step", { no: stepNo ?? t("live.unknownStep") })}{" "}
                  · {action}
                </span>
              )}
              {r.starting && (
                <span className="text-sky-900/70 dark:text-sky-200/70">
                  · {t("live.starting")}
                </span>
              )}
              <span className="ml-auto shrink-0 text-xs text-sky-900/60 dark:text-sky-200/60">
                {r.step_count > 0 ? `${t("run.steps", { count: r.step_count })} · ` : ""}
                {elapsed(r.started_at)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
